import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, ArrowRight, Loader2, Image as ImageIcon, X } from "lucide-react";

const initial = {
  photographer_name: "",
  email: "",
  location: "",
  caption: "",
  instagram_handle: "",
};

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB

export default function SubmitPhoto() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setPreview(result);
      const b64 = typeof result === "string" ? result.split(",")[1] : null;
      setBase64(b64);
    };
    reader.readAsDataURL(f);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setBase64(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.photographer_name || !form.email || !form.location || !form.caption) {
      toast.error("Please fill out all required fields");
      return;
    }
    if (!file || !base64) {
      toast.error("Please attach a photo");
      return;
    }
    if (!consent) {
      toast.error("You must grant permission for us to share your photo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/photos", {
        ...form,
        photo_filename: file.name,
        photo_data_base64: base64,
        photo_mime_type: file.type || "image/jpeg",
        consent: true,
      });
      setSuccess(true);
      toast.success("Photo submitted — thank you!");
      setForm(initial);
      clearFile();
      setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="grain section-pad" data-testid="photo-success">
        <div className="container-vg max-w-2xl text-center">
          <div className="w-20 h-20 mx-auto bg-green-500/10 border border-green-500/40 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <div className="overline mt-8 !text-green-400">Photo Received</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter mt-4">
            Thanks for your <span className="text-yellow-400">shot.</span>
          </h1>
          <p className="text-zinc-400 mt-6 text-lg leading-relaxed">
            We'll review your submission and, if featured, post it to our Facebook page with credit to you. Keep chasing.
          </p>
          <button onClick={() => setSuccess(false)} className="btn-secondary mt-10" data-testid="photo-submit-another">
            Submit another <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grain section-pad" data-testid="submit-photo-page">
      <div className="container-vg max-w-4xl">
        <div className="overline">Community Photo Submission</div>
        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4">
          Send us your <span className="text-yellow-400">weather shot.</span>
        </h1>
        <p className="text-zinc-400 mt-6 text-lg max-w-2xl leading-relaxed">
          Caught a wild sky? Tornado, mammatus, lightning, hail, a wall cloud over the corn —
          submit your photo and we may feature it on our Facebook page with credit to you.
        </p>

        <form onSubmit={onSubmit} className="mt-12 space-y-10" data-testid="photo-form">
          {/* Uploader */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">01.</span>
              <h2 className="font-heading text-2xl font-semibold">Your Photo</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {preview ? (
              <div className="relative border border-white/10" data-testid="photo-preview">
                <img src={preview} alt="Preview" className="w-full max-h-[520px] object-contain bg-black" />
                <button
                  type="button"
                  onClick={clearFile}
                  className="absolute top-3 right-3 bg-black/70 backdrop-blur border border-white/10 text-white p-2 hover:border-yellow-400 hover:text-yellow-400"
                  aria-label="Remove photo"
                  data-testid="photo-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="bg-[#0d0f12] border-t border-white/10 p-4 flex items-center gap-3">
                  <ImageIcon className="w-4 h-4 text-yellow-400" />
                  <span className="font-mono text-xs uppercase tracking-wider text-zinc-300 truncate">{file?.name}</span>
                  <span className="ml-auto font-mono text-xs text-zinc-500">{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ""}</span>
                </div>
              </div>
            ) : (
              <label
                className="block border-2 border-dashed border-white/15 hover:border-yellow-400 transition-colors p-12 text-center cursor-pointer"
                data-testid="photo-dropzone"
              >
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFile}
                  data-testid="photo-input"
                />
                <Upload className="w-9 h-9 text-yellow-400 mx-auto" />
                <div className="font-heading text-lg mt-4 text-white">Click or drop a photo here</div>
                <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-2">
                  JPG, PNG, HEIC, WEBP · Max 8 MB
                </div>
              </label>
            )}
          </div>

          {/* Photographer info */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-yellow-400 text-sm">02.</span>
              <h2 className="font-heading text-2xl font-semibold">About the Shot</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Your Name *" name="photographer_name" value={form.photographer_name} onChange={update} />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={update} />
              <Field label="Location *" name="location" value={form.location} onChange={update} placeholder="City, State (e.g., Norman, OK)" />
              <Field label="Instagram Handle (optional)" name="instagram_handle" value={form.instagram_handle} onChange={update} placeholder="@yourhandle" />
            </div>

            <div className="mt-6">
              <label className="overline mb-2 block !text-zinc-400">Caption / Story *</label>
              <textarea
                rows={4}
                value={form.caption}
                onChange={(e) => update("caption", e.target.value)}
                className="input-vg resize-none"
                placeholder="When was this taken? What were you witnessing? Any details we should know…"
                data-testid="photo-field-caption"
              />
            </div>
          </div>

          {/* Consent */}
          <div className="card-vg p-6 flex items-start gap-4">
            <input
              type="checkbox"
              id="photo-consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-5 h-5 accent-yellow-400 cursor-pointer"
              data-testid="photo-consent"
            />
            <label htmlFor="photo-consent" className="text-sm text-zinc-300 leading-relaxed cursor-pointer">
              I confirm I am the photographer (or have rights to this image) and I grant Vortex Ghost permission to
              repost it on Facebook and other social channels with photo credit to me. *
            </label>
          </div>

          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider max-w-md">
              We review every submission. Featured shots get tagged @ your Instagram (if provided).
            </p>
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="photo-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>Submit Photo <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="overline mb-2 block !text-zinc-400">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="input-vg"
        placeholder={placeholder}
        data-testid={`photo-field-${name}`}
      />
    </div>
  );
}
